import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { api, formatApiError, fileSrc } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import RibCard from "@/components/RibCard";
import PortalSettingsCard from "@/components/PortalSettingsCard";
import { eur } from "@/components/LineItemsEditor";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Settings as SettingsIcon, Loader2, Save, UserPlus, Trash2, Users, Upload, Image as ImageIcon, Plus, Download, ShieldAlert, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { usePageGuard } from "@/lib/unsavedGuard";

export default function Settings() {
  const guardRef = usePageGuard();
  const { user } = useAuth();
  const location = useLocation();
  const initialTab = location.pathname === "/facturation-electronique" ? "einvoicing" : (new URLSearchParams(location.search).get("tab") || "company");
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [uOpen, setUOpen] = useState(false);
  const [uForm, setUForm] = useState({ name: "", email: "", password: "", role: "employee", phone: "" });

  useEffect(() => {
    api.get("/settings").then((r) => setS(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
    if (["owner", "admin"].includes(user?.role)) api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  }, [user]);

  const set = (k) => (e) => setS({ ...s, [k]: e.target.value });

  const save = async () => {
    setSaving(true);
    try {
      const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
      const payload = { ...s, urssaf_rate: num(s.urssaf_rate), cfp_rate: num(s.cfp_rate), liberatoire_rate: num(s.liberatoire_rate), mileage_rate: num(s.mileage_rate), fixed_charges: (s.fixed_charges || []).map((c) => ({ label: c.label || "", amount: Number(c.amount) || 0 })) };
      const { data } = await api.put("/settings", payload);
      setS(data);
      toast.success("Paramètres enregistrés");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  const createUser = async () => {
    if (!uForm.name || !uForm.email || !uForm.password) return toast.error("Nom, email et mot de passe requis");
    try {
      await api.post("/users", uForm);
      toast.success("Utilisateur créé");
      setUOpen(false);
      setUForm({ name: "", email: "", password: "", role: "employee", phone: "" });
      api.get("/users").then((r) => setUsers(r.data));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const delUser = async (id) => {
    try { await api.delete(`/users/${id}`); toast.success("Utilisateur supprimé"); api.get("/users").then((r) => setUsers(r.data)); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!s) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>;

  return (
    <div ref={guardRef}>
      <PageHeader
        title="Paramètres"
        subtitle="Informations de l'entreprise, réutilisées dans les devis et factures"
        icon={SettingsIcon}
        actions={<Button onClick={save} disabled={saving} data-testid="btn-save-settings" className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold">{saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-1.5" />}Enregistrer</Button>}
      />

      <PublicLinkCard />

      <Tabs defaultValue={initialTab}>
        <TabsList className="bg-white border border-slate-200 mb-4 flex flex-wrap h-auto gap-1">
          <TabsTrigger value="account" data-testid="tab-account">Compte</TabsTrigger>
          <TabsTrigger value="company" data-testid="tab-company">Entreprise</TabsTrigger>
          <TabsTrigger value="messages" data-testid="tab-messages">Messages</TabsTrigger>
          <TabsTrigger value="emails" data-testid="tab-emails">Emails</TabsTrigger>
          <TabsTrigger value="billing" data-testid="tab-billing">Facturation</TabsTrigger>
          <TabsTrigger value="design" data-testid="tab-design">Design</TabsTrigger>
          <TabsTrigger value="charges" data-testid="tab-charges">Charges</TabsTrigger>
          <TabsTrigger value="trajets" data-testid="tab-trajets">Trajets</TabsTrigger>
          <TabsTrigger value="gabarits" data-testid="tab-gabarits">Gabarits</TabsTrigger>
          <TabsTrigger value="einvoicing" data-testid="tab-einvoicing">Facturation électronique</TabsTrigger>
          <TabsTrigger value="portail" data-testid="tab-portail">Portail client</TabsTrigger>
          <TabsTrigger value="availability" data-testid="tab-availability">Disponibilités</TabsTrigger>
          <TabsTrigger value="privacy" data-testid="tab-privacy">Confidentialité</TabsTrigger>
        </TabsList>

        <TabsContent value="company">
          <div className="bg-white border border-slate-200 rounded-xl p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Fld label="Nom commercial"><Input data-testid="input-commercial-name" value={s.commercial_name || ""} onChange={set("commercial_name")} className="bg-slate-50 border-slate-200" /></Fld>
            <Fld label="Nom / prénom entrepreneur"><Input value={s.owner_name || ""} onChange={set("owner_name")} className="bg-slate-50 border-slate-200" /></Fld>
            <Fld label="Adresse" full><Input value={s.address || ""} onChange={set("address")} className="bg-slate-50 border-slate-200" /></Fld>
            <Fld label="Téléphone"><Input value={s.phone || ""} onChange={set("phone")} className="bg-slate-50 border-slate-200" /></Fld>
            <Fld label="Email"><Input value={s.email || ""} onChange={set("email")} className="bg-slate-50 border-slate-200" /></Fld>
            <Fld label="SIRET"><Input value={s.siret || ""} onChange={set("siret")} className="bg-slate-50 border-slate-200" /></Fld>
            <Fld label="Logo (URL)"><Input value={s.logo || ""} onChange={set("logo")} className="bg-slate-50 border-slate-200" /></Fld>
            <Fld label="Ville météo (tableau de bord)"><Input data-testid="input-weather-city" value={s.weather_city || ""} onChange={set("weather_city")} placeholder="Ex : Paris" className="bg-slate-50 border-slate-200" /></Fld>
            <Fld label="Note de règlement (texte libre, facultatif)" full><Textarea value={s.bank_details || ""} onChange={set("bank_details")} placeholder="Ex : Paiement à réception de facture" className="bg-slate-50 border-slate-200" /></Fld>
          </div>
          <div className="mt-6"><RibCard s={s} setS={setS} onSave={save} /></div>
        </TabsContent>

        <TabsContent value="messages">
          <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
            <div>
              <h3 className="font-heading text-base font-semibold text-slate-900">Message d'accompagnement des emails</h3>
              <p className="text-xs text-slate-500 mt-1">Texte inséré dans le corps de l'email lors de l'envoi d'une facture. Laissez vide pour utiliser le message par défaut.</p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-500">Message (facture envoyée par email)</Label>
              <Textarea data-testid="input-email-intro" value={s.email_intro_message || ""} onChange={set("email_intro_message")} rows={4} placeholder="Ex : Bonjour, veuillez trouver ci-joint votre facture. Merci de votre confiance." className="bg-slate-50 border-slate-200" />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="emails">
          <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-5">
            <div>
              <h3 className="font-heading text-base font-semibold text-slate-900">Emails</h3>
              <p className="text-xs text-slate-500 mt-1">Signature et options des emails envoyés à vos clients (via Resend).</p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-500">Signature d'email</Label>
              <Textarea data-testid="input-email-signature" value={s.email_signature || ""} onChange={set("email_signature")} rows={3} placeholder="Ex : Mon entreprise — 06 12 34 56 78" className="bg-slate-50 border-slate-200" />
            </div>
            <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-3">
              <div><Label className="text-sm text-slate-700">Joindre le RIB aux emails de facture impayée</Label><p className="text-[11px] text-slate-500">Ajoute vos coordonnées bancaires automatiquement.</p></div>
              <Switch checked={s.attach_rib_unpaid_emails ?? true} onCheckedChange={(v) => setS({ ...s, attach_rib_unpaid_emails: v })} data-testid="switch-attach-rib-emails" />
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-3 text-sm">
              <div className="text-slate-700">Adresse de réponse (reply-to)</div>
              <div className="text-slate-500 text-xs mt-0.5">{s.email || "Renseignez l'email de l'entreprise dans l'onglet Entreprise."}</div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-5 mt-6" data-testid="review-settings-card">
            <div>
              <h3 className="font-heading text-base font-semibold text-slate-900">Demande d'avis Google</h3>
              <p className="text-xs text-slate-500 mt-1">Après une prestation terminée et payée, invitez vos clients à laisser un avis. Le bouton de l'e-mail pointe vers VOTRE lien Google.</p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-500">Lien d'avis Google</Label>
              <Input data-testid="input-google-review-link" value={s.google_review_link || ""} onChange={set("google_review_link")} placeholder="https://g.page/r/..." className="bg-slate-50 border-slate-200" />
            </div>
            <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-3">
              <div><Label className="text-sm text-slate-700">Envoi automatique de la demande d'avis</Label><p className="text-[11px] text-slate-500">Envoyée automatiquement après le délai ci-dessous (sinon manuel depuis le RDV).</p></div>
              <Switch checked={!!s.review_request_enabled} onCheckedChange={(v) => setS({ ...s, review_request_enabled: v })} data-testid="switch-review-enabled" />
            </div>
            <div className="max-w-xs">
              <Fld label="Délai après la prestation (jours)"><Input type="number" min="0" data-testid="input-review-delay" value={s.review_delay_days ?? 1} onChange={(e) => setS({ ...s, review_delay_days: Number(e.target.value) })} className="bg-slate-50 border-slate-200" /></Fld>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="design">
          <DocumentVisualsCard s={s} setS={setS} />
        </TabsContent>

        <TabsContent value="charges">
          <FixedChargesCard s={s} setS={setS} />
        </TabsContent>

        <TabsContent value="trajets">
          <TrajetsCard s={s} setS={setS} />
        </TabsContent>

        <TabsContent value="gabarits">
          <GabaritsCard s={s} setS={setS} />
        </TabsContent>

        <TabsContent value="einvoicing">
          <EInvoicingCard />
        </TabsContent>

        <TabsContent value="portail">
          <PortalSettingsCard />
        </TabsContent>

        <TabsContent value="billing">
          <div className="bg-white border border-slate-200 rounded-xl p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Fld label="Taux de cotisation URSSAF (%)">
              <Input type="number" step="0.1" data-testid="input-urssaf-rate" value={s.urssaf_rate ?? ""} onChange={set("urssaf_rate")} className="bg-slate-50 border-slate-200" />
            </Fld>
            <Fld label="Taux CFP — formation professionnelle (%)">
              <Input type="number" step="0.1" data-testid="input-cfp-rate" value={s.cfp_rate ?? ""} onChange={set("cfp_rate")} className="bg-slate-50 border-slate-200" />
            </Fld>
            <Fld label="Versement libératoire de l'impôt">
              <select data-testid="select-liberatoire-enabled" value={s.liberatoire_enabled ? "1" : "0"} onChange={(e) => setS({ ...s, liberatoire_enabled: e.target.value === "1" })} className="w-full h-10 rounded-md border border-slate-200 bg-slate-50 px-3 text-sm text-slate-900">
                <option value="0">Désactivé</option>
                <option value="1">Activé</option>
              </select>
            </Fld>
            <Fld label="Taux versement libératoire (%)">
              <Input type="number" step="0.1" data-testid="input-liberatoire-rate" value={s.liberatoire_rate ?? ""} onChange={set("liberatoire_rate")} className="bg-slate-50 border-slate-200" />
            </Fld>
            <Fld label="Objectif mensuel de CA (€)"><Input type="number" data-testid="input-monthly-objective" value={s.monthly_objective ?? ""} onChange={set("monthly_objective")} className="bg-slate-50 border-slate-200" /></Fld>
            <div />
            <Fld label="Préfixe numérotation devis"><Input value={s.quote_prefix || ""} onChange={set("quote_prefix")} className="bg-slate-50 border-slate-200" /></Fld>
            <Fld label="Préfixe numérotation factures"><Input value={s.invoice_prefix || ""} onChange={set("invoice_prefix")} className="bg-slate-50 border-slate-200" /></Fld>
            <Fld label="Mentions légales" full><Textarea value={s.legal_mentions || ""} onChange={set("legal_mentions")} rows={3} className="bg-slate-50 border-slate-200" /></Fld>
            <Fld label="Conditions générales" full><Textarea value={s.terms || ""} onChange={set("terms")} rows={3} className="bg-slate-50 border-slate-200" /></Fld>
            <p className="col-span-full text-xs text-slate-500">La facturation électronique agréée sera connectable ultérieurement (Phase 4). Cette application n'est pas une plateforme agréée.</p>
          </div>
        </TabsContent>

        <TabsContent value="availability">
          <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-5">
            <div>
              <Label className="text-xs text-slate-500 mb-2 block">Jours d'ouverture</Label>
              <div className="flex flex-wrap gap-2">
                {[[1, "Lun"], [2, "Mar"], [3, "Mer"], [4, "Jeu"], [5, "Ven"], [6, "Sam"], [7, "Dim"]].map(([n, lbl]) => {
                  const openDays = s.open_days || [1, 2, 3, 4, 5];
                  const on = openDays.includes(n);
                  return (
                    <button key={n} type="button" data-testid={`open-day-${n}`}
                      onClick={() => setS({ ...s, open_days: on ? openDays.filter((d) => d !== n) : [...openDays, n].sort() })}
                      className={`px-3 py-2 rounded-lg border text-sm transition ${on ? "bg-emerald-500 text-black border-emerald-500 font-semibold" : "bg-slate-50 text-slate-500 border-slate-200"}`}>
                      {lbl}
                    </button>
                  );
                })}
              </div>
              <p className="text-[11px] text-slate-500 mt-2">Les jours non cochés apparaissent en gris (fermé) dans le calendrier public. Par défaut : lundi au vendredi.</p>
            </div>
            <div className="grid grid-cols-2 gap-4 max-w-sm">
              <Fld label="Heure d'ouverture"><Input type="number" min="0" max="23" data-testid="input-open-hour" value={s.open_hour ?? 9} onChange={(e) => setS({ ...s, open_hour: Number(e.target.value) })} className="bg-slate-50 border-slate-200" /></Fld>
              <Fld label="Heure de fermeture"><Input type="number" min="0" max="23" data-testid="input-close-hour" value={s.close_hour ?? 18} onChange={(e) => setS({ ...s, close_hour: Number(e.target.value) })} className="bg-slate-50 border-slate-200" /></Fld>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-3 max-w-sm">
              <div><Label className="text-sm text-slate-700">Mode « 1 véhicule par jour »</Label><p className="text-[11px] text-slate-500">Une journée avec un RDV confirmé devient complète (rouge).</p></div>
              <Switch checked={s.one_vehicle_per_day ?? true} onCheckedChange={(v) => setS({ ...s, one_vehicle_per_day: v })} data-testid="switch-one-vehicle" />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="account" className="space-y-6">
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <h3 className="font-heading text-base font-semibold text-slate-900 mb-4">Mon compte</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
              <div><div className="text-xs text-slate-500">Nom</div><div className="text-slate-900 font-medium" data-testid="account-name">{user?.name || "—"}</div></div>
              <div><div className="text-xs text-slate-500">Email</div><div className="text-slate-900 font-medium" data-testid="account-email">{user?.email || "—"}</div></div>
              <div><div className="text-xs text-slate-500">Rôle</div><div className="text-slate-900 font-medium capitalize">{user?.role === "owner" ? "Propriétaire" : user?.role === "admin" ? "Administrateur" : "Employé"}</div></div>
            </div>
          </div>
          <ChangePasswordCard />
          <GoogleCalendarCard />
          {["owner", "admin"].includes(user?.role) && (
            <div className="bg-white border border-slate-200 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading text-base font-semibold text-slate-900 flex items-center gap-2"><Users className="w-4 h-4 text-emerald-600" /> Comptes utilisateurs</h3>
                <Button size="sm" onClick={() => setUOpen(true)} data-testid="btn-new-user" className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold"><UserPlus className="w-4 h-4 mr-1.5" /> Ajouter</Button>
              </div>
              <div className="space-y-2">
                {users.map((u) => (
                  <div key={u.id} className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-lg p-3">
                    <div>
                      <div className="text-sm text-slate-900">{u.name} {u.role === "owner" ? <Badge variant="outline" className="ml-1 text-emerald-600 border-emerald-500/30 text-[10px]">Propriétaire</Badge> : u.role === "admin" ? <Badge variant="outline" className="ml-1 text-sky-600 border-sky-500/30 text-[10px]">Administrateur</Badge> : null}</div>
                      <div className="text-xs text-slate-500">{u.email} · {u.role}</div>
                    </div>
                    {u.role !== "owner" && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild><Button size="sm" variant="outline" data-testid={`btn-delete-user-${u.id}`} className="border-red-500/30 bg-red-500/5 text-red-600 hover:bg-red-500/10 h-8 px-2"><Trash2 className="w-3.5 h-3.5" /></Button></AlertDialogTrigger>
                        <AlertDialogContent className="bg-white border-slate-200">
                          <AlertDialogHeader><AlertDialogTitle className="text-slate-900">Supprimer cet utilisateur ?</AlertDialogTitle><AlertDialogDescription>Action irréversible.</AlertDialogDescription></AlertDialogHeader>
                          <AlertDialogFooter><AlertDialogCancel className="bg-transparent border-slate-200">Annuler</AlertDialogCancel><AlertDialogAction onClick={() => delUser(u.id)} className="bg-red-500 hover:bg-red-600">Supprimer</AlertDialogAction></AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="privacy" className="space-y-6">
          <PrivacyCard />
        </TabsContent>
      </Tabs>

      <Dialog open={uOpen} onOpenChange={setUOpen}>
        <DialogContent className="bg-white border-slate-200 max-w-md">
          <DialogHeader><DialogTitle className="text-slate-900">Nouvel utilisateur</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Fld label="Nom"><Input data-testid="input-user-name" value={uForm.name} onChange={(e) => setUForm({ ...uForm, name: e.target.value })} className="bg-slate-50 border-slate-200" /></Fld>
            <Fld label="Email"><Input data-testid="input-user-email" value={uForm.email} onChange={(e) => setUForm({ ...uForm, email: e.target.value })} className="bg-slate-50 border-slate-200" /></Fld>
            <Fld label="Mot de passe"><Input type="password" data-testid="input-user-password" value={uForm.password} onChange={(e) => setUForm({ ...uForm, password: e.target.value })} className="bg-slate-50 border-slate-200" /></Fld>
            <Fld label="Rôle">
              <Select value={uForm.role} onValueChange={(v) => setUForm({ ...uForm, role: v })}>
                <SelectTrigger data-testid="select-user-role" className="bg-slate-50 border-slate-200"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white border-slate-200 text-slate-700">
                  <SelectItem value="employee">Employé</SelectItem>
                  <SelectItem value="client">Client</SelectItem>
                  <SelectItem value="admin">Administrateur</SelectItem>
                </SelectContent>
              </Select>
            </Fld>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setUOpen(false)}>Annuler</Button>
            <Button onClick={createUser} data-testid="btn-save-user" className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold">Créer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Fld({ label, children, full }) {
  return <div className={`space-y-1.5 ${full ? "sm:col-span-2" : ""}`}><Label className="text-xs text-slate-500">{label}</Label>{children}</div>;
}

function ChangePasswordCard() {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (form.new_password.length < 8) return toast.error("Le nouveau mot de passe doit contenir au moins 8 caractères");
    if (form.new_password !== form.confirm) return toast.error("Les mots de passe ne correspondent pas");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: form.current_password, new_password: form.new_password });
      toast.success("Mot de passe modifié");
      setForm({ current_password: "", new_password: "", confirm: "" });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 max-w-md space-y-4">
      <h3 className="font-heading text-base font-semibold text-slate-900">Changer mon mot de passe</h3>
      <Fld label="Mot de passe actuel"><Input type="password" data-testid="input-current-password" value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })} className="bg-slate-50 border-slate-200" /></Fld>
      <Fld label="Nouveau mot de passe"><Input type="password" data-testid="input-new-password" value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} className="bg-slate-50 border-slate-200" /></Fld>
      <Fld label="Confirmer le nouveau mot de passe"><Input type="password" data-testid="input-confirm-password" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} className="bg-slate-50 border-slate-200" /></Fld>
      <Button onClick={submit} disabled={busy} data-testid="btn-change-password" className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold">{busy && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Mettre à jour</Button>
    </div>
  );
}


function GoogleCalendarCard() {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = () => api.get("/oauth/calendar/status").then((r) => setSt(r.data)).catch(() => setSt({ configured: false, connected: false }));
  useEffect(() => { refresh(); }, []);

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/oauth/calendar/authorize-url");
      window.location.href = data.authorize_url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusy(false); }
  };

  const disconnect = async () => {
    setBusy(true);
    try {
      await api.post("/oauth/calendar/disconnect");
      toast.success("Agenda Google déconnecté");
      refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  if (!st) return null;

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 max-w-md space-y-4" data-testid="gcal-card">
      <div className="flex items-center gap-2">
        <CalendarDays className="w-4 h-4 text-emerald-600" />
        <h3 className="font-heading text-base font-semibold text-slate-900">Google Agenda</h3>
      </div>
      <p className="text-xs text-slate-500">Connectez votre agenda Google pour y pousser automatiquement vos rendez-vous (sens unique : l'application → Google).</p>
      {!st.configured ? (
        <p className="text-[11px] text-red-600" data-testid="gcal-not-configured">La synchronisation Google Agenda n'est pas configurée sur le serveur.</p>
      ) : st.connected ? (
        <div className="space-y-3">
          <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2">
            <span className="h-2 w-2 rounded-full bg-emerald-500" />
            <span className="text-sm text-slate-700" data-testid="gcal-connected-email">Connecté{st.email ? ` · ${st.email}` : ""}</span>
          </div>
          <Button onClick={disconnect} disabled={busy} variant="outline" data-testid="btn-gcal-disconnect" className="border-red-500/30 bg-red-500/5 text-red-600 hover:bg-red-500/10">{busy && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Déconnecter</Button>
        </div>
      ) : (
        <Button onClick={connect} disabled={busy} data-testid="btn-gcal-connect" className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold">{busy && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Connecter Google Agenda</Button>
      )}
    </div>
  );
}


function DocumentVisualsCard({ s, setS }) {
  const FONTS = [["Helvetica", "Helvetica (sans-serif)"], ["Times-Roman", "Times (serif)"], ["Courier", "Courier (mono)"]];
  const align = s.doc_header_align || "left";
  const primary = s.doc_primary_color || "#0f9d6b";
  const secondary = s.doc_secondary_color || "#e11d2a";
  const fontFamily = (s.doc_font || "Helvetica") === "Times-Roman" ? "Georgia, serif" : (s.doc_font === "Courier" ? "monospace" : "Arial, sans-serif");
  return (
    <div className="mt-6 bg-white border border-slate-200 rounded-xl p-6" data-testid="document-visuals-card">
      <h3 className="font-heading text-base font-semibold text-slate-900 mb-1">Design des documents (devis & factures)</h3>
      <p className="text-xs text-slate-500 mb-5">Personnalisez vos PDF. Les nouveaux documents utiliseront ce design ; les PDF déjà générés restent inchangés.</p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-500">Couleur principale</Label>
              <div className="flex items-center gap-2">
                <input type="color" value={primary} onChange={(e) => setS({ ...s, doc_primary_color: e.target.value })} data-testid="doc-primary-color" className="w-10 h-9 rounded border border-slate-200 cursor-pointer" />
                <Input value={primary} onChange={(e) => setS({ ...s, doc_primary_color: e.target.value })} className="bg-slate-50 border-slate-200 font-mono text-xs" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-500">Couleur secondaire</Label>
              <div className="flex items-center gap-2">
                <input type="color" value={secondary} onChange={(e) => setS({ ...s, doc_secondary_color: e.target.value })} data-testid="doc-secondary-color" className="w-10 h-9 rounded border border-slate-200 cursor-pointer" />
                <Input value={secondary} onChange={(e) => setS({ ...s, doc_secondary_color: e.target.value })} className="bg-slate-50 border-slate-200 font-mono text-xs" />
              </div>
            </div>
          </div>
          <button type="button" onClick={() => setS({ ...s, doc_primary_color: "#0f9d6b", doc_secondary_color: "#e11d2a" })} data-testid="doc-reset-colors" className="text-[11px] text-emerald-600 hover:underline">Réinitialiser aux couleurs par défaut (vert / rouge)</button>

          <div className="space-y-1.5">
            <Label className="text-xs text-slate-500">Police</Label>
            <Select value={s.doc_font || "Helvetica"} onValueChange={(v) => setS({ ...s, doc_font: v })}>
              <SelectTrigger data-testid="doc-font" className="bg-slate-50 border-slate-200"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white border-slate-200 text-slate-700">{FONTS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs text-slate-500">Alignement de l'en-tête</Label>
            <div className="flex gap-2">
              {[["left", "Gauche"], ["center", "Centré"], ["right", "Droite"]].map(([k, l]) => (
                <button key={k} type="button" onClick={() => setS({ ...s, doc_header_align: k })} data-testid={`doc-align-${k}`}
                  className={`flex-1 py-2 rounded-lg border text-sm transition ${align === k ? "bg-emerald-500 text-black border-emerald-500 font-semibold" : "bg-white border-slate-200 text-slate-500"}`}>{l}</button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 pt-2">
            <ImageUploadField label="Grand logo (en-tête)" hint="Affiché en haut du document." value={s.logo || ""} onChange={(url) => setS({ ...s, logo: url })} enabled={s.show_logo_on_documents ?? true} onToggle={(v) => setS({ ...s, show_logo_on_documents: v })} testid="logo" />
            <ImageUploadField label="Tampon / signature" hint="Affiché en bas à droite." value={s.stamp || ""} onChange={(url) => setS({ ...s, stamp: url })} enabled={s.show_stamp_on_documents ?? true} onToggle={(v) => setS({ ...s, show_stamp_on_documents: v })} testid="stamp" />
          </div>
        </div>

        {/* Live preview */}
        <div className="space-y-2">
          <Label className="text-xs text-slate-500">Aperçu en direct</Label>
          <div className="border border-slate-200 rounded-lg p-5 bg-white shadow-sm" style={{ fontFamily }} data-testid="doc-preview">
            <div style={{ textAlign: align }}>
              {(s.show_logo_on_documents ?? true) && s.logo && <img src={fileSrc(s.logo)} alt="" className="h-10 inline-block mb-1 object-contain" />}
              <div style={{ color: primary, fontWeight: 700, fontSize: 18 }}>{s.commercial_name || "Mon entreprise"}</div>
              <div className="text-[10px] text-slate-500">{[s.owner_name, s.address, s.phone].filter(Boolean).join(" · ") || "Adresse · Téléphone"}</div>
            </div>
            <div className="flex justify-between mt-3 text-[11px]">
              <div><span style={{ color: secondary, fontWeight: 700 }}>FACTURE</span><br />N° FAC-2026-0001<br />Date : {new Date().toLocaleDateString("fr-FR")}</div>
              <div className="text-right"><b>Client</b><br />Jean Dupont<br />Golf GTI</div>
            </div>
            <table className="w-full mt-3 text-[10px] border-collapse">
              <thead><tr style={{ background: primary, color: "#fff" }}><th className="text-left p-1">Description</th><th className="p-1">Qté</th><th className="p-1">Total</th></tr></thead>
              <tbody><tr className="border-b border-slate-100"><td className="p-1">Prestation detailing</td><td className="p-1 text-center">1</td><td className="p-1 text-right">150,00 €</td></tr></tbody>
            </table>
            <div className="text-right text-[11px] mt-2" style={{ borderTop: `2px solid ${primary}`, paddingTop: 4 }}><b>TOTAL : 150,00 €</b></div>
            {(s.show_stamp_on_documents ?? true) && s.stamp && <div className="flex justify-end mt-2"><img src={fileSrc(s.stamp)} alt="" className="h-12 object-contain" /></div>}
          </div>
        </div>
      </div>
    </div>
  );
}

function ImageUploadField({ label, hint, value, onChange, enabled, onToggle, testid }) {
  const [uploading, setUploading] = useState(false);
  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Image téléversée");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setUploading(false); }
  };
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-sm text-slate-700">{label}</Label>
        <Switch checked={enabled} onCheckedChange={onToggle} data-testid={`${testid}-toggle`} />
      </div>
      {hint && <p className="text-[11px] text-slate-500">{hint}</p>}
      <div className="flex items-center gap-3">
        <div className="w-24 h-24 rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden shrink-0">
          {value ? <img src={fileSrc(value)} alt="" className="w-full h-full object-contain" data-testid={`${testid}-preview`} /> : <ImageIcon className="w-6 h-6 text-slate-300" />}
        </div>
        <div className="space-y-2">
          <label className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-lg border border-slate-200 bg-white hover:border-emerald-500/40 cursor-pointer text-slate-700" data-testid={`${testid}-upload`}>
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4 text-emerald-600" />} Téléverser
            <input type="file" accept="image/*" className="hidden" onChange={handle} />
          </label>
          {value && <button type="button" onClick={() => onChange("")} className="block text-[11px] text-red-600 hover:underline" data-testid={`${testid}-remove`}>Retirer</button>}
        </div>
      </div>
    </div>
  );
}

function DocumentVisualsCardOldRemoved() { return null; }

function FixedChargesCard({ s, setS }) {
  const list = s.fixed_charges || [];
  const update = (i, k, v) => setS({ ...s, fixed_charges: list.map((c, idx) => (idx === i ? { ...c, [k]: v } : c)) });
  const add = () => setS({ ...s, fixed_charges: [...list, { label: "", amount: "" }] });
  const remove = (i) => setS({ ...s, fixed_charges: list.filter((_, idx) => idx !== i) });
  const total = list.reduce((sum, c) => sum + (Number(c.amount) || 0), 0);
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4" data-testid="fixed-charges-card">
      <div>
        <h3 className="font-heading text-base font-semibold text-slate-900">Charges fixes mensuelles</h3>
        <p className="text-xs text-slate-500 mt-1">Charges récurrentes (assurance, abonnements, loyer...). Elles servent au calcul du résultat dans la Comptabilité mais ne sont JAMAIS déduites du CA à déclarer à l'URSSAF. Les charges variables se gèrent au mois via la page Dépenses.</p>
      </div>
      <div className="space-y-2">
        {list.map((c, i) => (
          <div key={i} className="flex items-center gap-2" data-testid={`fixed-charge-${i}`}>
            <Input value={c.label || ""} onChange={(e) => update(i, "label", e.target.value)} placeholder="Libellé (ex : Assurance)" data-testid={`fixed-charge-label-${i}`} className="bg-slate-50 border-slate-200 flex-1" />
            <Input type="number" step="0.01" value={c.amount ?? ""} onChange={(e) => update(i, "amount", e.target.value)} placeholder="€ / mois" data-testid={`fixed-charge-amount-${i}`} className="bg-slate-50 border-slate-200 w-28 sm:w-32" />
            <Button variant="outline" size="sm" onClick={() => remove(i)} data-testid={`fixed-charge-remove-${i}`} className="border-red-500/30 bg-red-500/5 text-red-600 hover:bg-red-500/10 shrink-0"><Trash2 className="w-4 h-4" /></Button>
          </div>
        ))}
        {list.length === 0 && <p className="text-sm text-slate-500">Aucune charge fixe. Ajoutez-en une ci-dessous.</p>}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-slate-100">
        <Button variant="outline" size="sm" onClick={add} data-testid="btn-add-fixed-charge" className="border-slate-200"><Plus className="w-4 h-4 mr-1.5" /> Ajouter une charge fixe</Button>
        <div className="text-sm text-slate-500">Total mensuel : <span className="font-mono font-semibold text-slate-900" data-testid="fixed-charges-total">{eur(total)}</span></div>
      </div>
      <p className="text-[11px] text-slate-400">Cliquez sur « Enregistrer » en haut de la page pour sauvegarder.</p>
    </div>
  );
}

function TrajetsCard({ s, setS }) {
  const [km, setKm] = useState("");
  const rate = Number(s.mileage_rate) || 0;
  const amount = (Number(km) || 0) * rate;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-5" data-testid="trajets-card">
      <div>
        <h3 className="font-heading text-base font-semibold text-slate-900">Trajets & indemnités kilométriques</h3>
        <p className="text-xs text-slate-500 mt-1">Définissez votre barème au kilomètre pour estimer le coût de vos déplacements. Ces trajets peuvent être enregistrés comme charges variables via la page Dépenses.</p>
      </div>
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-3" data-testid="travel-fee-config">
        <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Frais de déplacement facturés au client</Label>
        <Fld label="Adresse de départ (calcul auto de distance)"><Input data-testid="input-travel-origin" value={s.travel_origin_address ?? ""} onChange={(e) => setS({ ...s, travel_origin_address: e.target.value })} placeholder="Vide = adresse de l'entreprise" className="bg-white border-slate-200" /></Fld>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="space-y-1.5"><Label className="text-xs text-slate-500">Mode</Label>
            <Select value={s.travel_fee_mode || "aucun"} onValueChange={(v) => setS({ ...s, travel_fee_mode: v })}>
              <SelectTrigger data-testid="select-travel-mode" className="bg-white border-slate-200"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white border-slate-200 text-slate-700">
                <SelectItem value="aucun">Aucun frais</SelectItem>
                <SelectItem value="forfait">Forfait fixe</SelectItem>
                <SelectItem value="km">Tarif au kilomètre</SelectItem>
                <SelectItem value="paliers">Paliers de distance</SelectItem>
                <SelectItem value="manuel">Saisie manuelle</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {s.travel_fee_mode === "forfait" && <Fld label="Forfait (€)"><Input type="number" step="0.01" data-testid="input-travel-flat" value={s.travel_flat_amount ?? ""} onChange={(e) => setS({ ...s, travel_flat_amount: e.target.value })} className="bg-white border-slate-200" /></Fld>}
          {s.travel_fee_mode === "km" && <Fld label="Tarif client (€ / km)"><Input type="number" step="0.01" data-testid="input-travel-per-km" value={s.travel_per_km ?? ""} onChange={(e) => setS({ ...s, travel_per_km: e.target.value })} className="bg-white border-slate-200" /></Fld>}
          <Fld label="× récupération + livraison"><Input type="number" step="0.5" data-testid="input-travel-recup" value={s.recuperation_multiplier ?? 2} onChange={(e) => setS({ ...s, recuperation_multiplier: e.target.value })} className="bg-white border-slate-200" /></Fld>
        </div>
        {s.travel_fee_mode === "km" && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
            <label className="flex items-center gap-2 text-sm text-slate-700 py-2"><Switch checked={s.travel_round_trip !== false} onCheckedChange={(v) => setS({ ...s, travel_round_trip: v })} data-testid="switch-travel-roundtrip" /> Aller-retour</label>
            <Fld label="Km offerts"><Input type="number" step="1" data-testid="input-travel-free-km" value={s.travel_free_km ?? ""} onChange={(e) => setS({ ...s, travel_free_km: e.target.value })} className="bg-white border-slate-200" /></Fld>
            <Fld label="Montant minimum (€)"><Input type="number" step="0.01" data-testid="input-travel-min" value={s.travel_min_amount ?? ""} onChange={(e) => setS({ ...s, travel_min_amount: e.target.value })} className="bg-white border-slate-200" /></Fld>
          </div>
        )}
        {s.travel_fee_mode === "paliers" && (
          <div className="space-y-2" data-testid="travel-tiers">
            <Label className="text-xs text-slate-500">Paliers (jusqu'à X km → montant €)</Label>
            {(s.travel_tiers || []).map((t, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input type="number" placeholder="≤ km" value={t.max_km ?? ""} onChange={(e) => setS({ ...s, travel_tiers: s.travel_tiers.map((x, j) => (j === i ? { ...x, max_km: e.target.value } : x)) })} data-testid={`tier-km-${i}`} className="w-24 bg-white border-slate-200" />
                <Input type="number" placeholder="€" value={t.amount ?? ""} onChange={(e) => setS({ ...s, travel_tiers: s.travel_tiers.map((x, j) => (j === i ? { ...x, amount: e.target.value } : x)) })} data-testid={`tier-amt-${i}`} className="w-24 bg-white border-slate-200" />
                <Button variant="outline" size="icon" onClick={() => setS({ ...s, travel_tiers: s.travel_tiers.filter((_, j) => j !== i) })} className="border-red-500/30 text-red-600 shrink-0"><Trash2 className="w-4 h-4" /></Button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={() => setS({ ...s, travel_tiers: [...(s.travel_tiers || []), { max_km: "", amount: "" }] })} data-testid="tier-add" className="border-slate-200"><Plus className="w-4 h-4 mr-1" /> Ajouter un palier</Button>
          </div>
        )}
        <p className="text-[11px] text-slate-400">« Chez le client » = 1 visite. « Récupération + livraison » = ce coefficient. Le calcul auto utilise OpenStreetMap (aucune clé à fournir) ; le montant reste modifiable dans chaque RDV.</p>
      </div>
      <div className="max-w-xs">
        <Fld label="Barème indemnités km — comptabilité (€ / km)"><Input type="number" step="0.01" data-testid="input-mileage-rate" value={s.mileage_rate ?? ""} onChange={(e) => setS({ ...s, mileage_rate: e.target.value })} className="bg-slate-50 border-slate-200" /></Fld>
      </div>
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 max-w-md">
        <Label className="text-xs text-slate-500">Calculateur rapide</Label>
        <div className="flex items-center gap-2 mt-2">
          <Input type="number" value={km} onChange={(e) => setKm(e.target.value)} placeholder="Distance (km)" data-testid="input-trip-km" className="bg-white border-slate-200 flex-1" />
          <span className="text-sm text-slate-500">× {rate.toFixed(2)} €</span>
        </div>
        <div className="mt-3 text-sm text-slate-500">Coût estimé : <span className="font-mono font-semibold text-emerald-600" data-testid="trip-estimate">{eur(amount)}</span></div>
      </div>
      <p className="text-[11px] text-slate-400">Cliquez sur « Enregistrer » en haut de la page pour sauvegarder le barème.</p>
    </div>
  );
}

function GabaritsCard({ s, setS }) {
  const cats = s.vehicle_categories || [];
  const add = () => setS({ ...s, vehicle_categories: [...cats, { id: `cat_${Date.now()}`, name: "" }] });
  const upd = (i, name) => setS({ ...s, vehicle_categories: cats.map((c, j) => (j === i ? { ...c, name } : c)) });
  const del = (i) => setS({ ...s, vehicle_categories: cats.filter((_, j) => j !== i) });
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-5" data-testid="gabarits-card">
      <div>
        <h3 className="font-heading text-base font-semibold text-slate-900">Gabarits de véhicule</h3>
        <p className="text-xs text-slate-500 mt-1">Créez vos catégories (ex : Citadine, Berline, SUV…). Vous définissez ensuite un supplément par gabarit sur chaque prestation.</p>
      </div>
      <div className="space-y-2 max-w-md">
        {cats.map((c, i) => (
          <div key={c.id} className="flex items-center gap-2" data-testid={`gabarit-row-${i}`}>
            <Input value={c.name} onChange={(e) => upd(i, e.target.value)} placeholder="Nom du gabarit" data-testid={`gabarit-name-${i}`} className="flex-1 bg-slate-50 border-slate-200" />
            <Button variant="outline" size="icon" onClick={() => del(i)} data-testid={`gabarit-del-${i}`} className="border-red-500/30 bg-red-500/5 text-red-600 hover:bg-red-500/10 shrink-0"><Trash2 className="w-4 h-4" /></Button>
          </div>
        ))}
        {cats.length === 0 && <p className="text-sm text-slate-500">Aucun gabarit. Ajoutez-en un.</p>}
      </div>
      <Button variant="outline" size="sm" onClick={add} data-testid="btn-add-gabarit" className="border-slate-200"><Plus className="w-4 h-4 mr-1.5" /> Ajouter un gabarit</Button>
      <p className="text-[11px] text-slate-400">Cliquez sur « Enregistrer » en haut de la page pour sauvegarder.</p>
    </div>
  );
}

function EInvoicingCard() {
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [cfg, setCfg] = useState({ api_token: "", environment: "sandbox", company_label: "" });
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const load = () => {
    api.get("/einvoicing/status").then((r) => {
      setStatus(r.data);
      setCfg((c) => ({ ...c, environment: r.data.environment || c.environment, company_label: c.company_label || r.data.company_label || "" }));
    }).catch(() => {});
    api.get("/einvoicing/transmissions").then((r) => setHistory(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const connected = !!status?.connected;

  // Enregistre le token (si saisi) puis effectue un VRAI test de connexion Pennylane.
  const saveAndTest = async () => {
    setSaving(true); setTestResult(null);
    try {
      await api.put("/einvoicing/pennylane/config", {
        api_token: cfg.api_token || undefined,
        environment: cfg.environment,
        company_label: cfg.company_label || undefined,
      });
      const { data } = await api.post("/einvoicing/pennylane/test");
      setTestResult({ ok: true, company: data.connected_company, env: data.verified_environment });
      toast.success("Connecté à Pennylane ✓");
      setCfg((c) => ({ ...c, api_token: "" })); // ne jamais conserver le token côté client
      load();
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail);
      setTestResult({ ok: false, error: msg });
      toast.error(msg);
      load();
    } finally { setSaving(false); }
  };
  const syncSuppliers = async () => {
    try { await api.post("/einvoicing/sync-suppliers"); toast.success("Synchronisation lancée"); load(); }
    catch (e) { toast.info(formatApiError(e.response?.data?.detail)); }
  };

  const LEGEND = [
    ["non_transmise", "Non transmise", "text-slate-500 bg-slate-100"],
    ["a_transmettre", "À transmettre", "text-indigo-700 bg-indigo-100"],
    ["transmise", "Transmise", "text-sky-700 bg-sky-100"],
    ["recue", "Reçue / acceptée", "text-emerald-700 bg-emerald-100"],
    ["rejetee", "Rejetée", "text-red-700 bg-red-100"],
    ["erreur", "Erreur", "text-amber-700 bg-amber-100"],
  ];

  return (
    <div className="space-y-6" data-testid="einvoicing-card">
      <div className={`rounded-xl border p-5 ${connected ? "border-emerald-300 bg-emerald-50" : "border-slate-200 bg-white"}`}>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <span className={`w-2.5 h-2.5 rounded-full ${connected ? "bg-emerald-500" : "bg-slate-300"}`} />
            <div>
              <div className="font-heading text-base font-semibold text-slate-900">Facturation électronique — Pennylane</div>
              <div className="text-sm" data-testid="einvoicing-connection-state">
                {connected ? <span className="text-emerald-700 font-medium">Activée — Pennylane connecté</span>
                  : <span className="text-slate-500 font-medium">Pennylane — Non connecté</span>}
              </div>
            </div>
          </div>
          <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${connected ? "text-emerald-700 bg-emerald-100" : "text-slate-500 bg-slate-100"}`}>
            {connected ? "Activée" : "Non connectée"}
          </span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mt-5 text-sm">
          <div><div className="text-[11px] text-slate-500">Plateforme</div><div className="font-medium text-slate-900" data-testid="einv-platform">{status?.platform_label || "Pennylane"}</div></div>
          <div><div className="text-[11px] text-slate-500">Environnement</div><div className="font-medium text-slate-900" data-testid="einv-environment">{status?.environment === "production" ? "Production" : "Sandbox"}</div></div>
          <div><div className="text-[11px] text-slate-500">Entreprise liée</div><div className="font-medium text-slate-900" data-testid="einv-company">{status?.connected_company || status?.company_label || "—"}</div></div>
          <div><div className="text-[11px] text-slate-500">Dernière synchro</div><div className="font-medium text-slate-900" data-testid="einv-lastsync">{status?.last_sync ? new Date(status.last_sync).toLocaleString("fr-FR") : "—"}</div></div>
          <div><div className="text-[11px] text-slate-500">À transmettre</div><div className="font-medium text-indigo-700" data-testid="einv-pending">{status?.pending_count ?? 0}</div></div>
          <div><div className="text-[11px] text-slate-500">Transmises</div><div className="font-medium text-sky-700" data-testid="einv-transmitted">{status?.transmitted_count ?? 0}</div></div>
          <div><div className="text-[11px] text-slate-500">En erreur</div><div className="font-medium text-red-600" data-testid="einv-errors">{status?.error_count ?? 0}</div></div>
          <div><div className="text-[11px] text-slate-500">Reçues</div><div className="font-medium text-emerald-700" data-testid="einv-received">{status?.received_count ?? 0}</div></div>
        </div>
      </div>

      {!connected && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-900" data-testid="einvoicing-notice">
          <p className="font-semibold mb-1">Connexion à une plateforme agréée requise pour la conformité.</p>
          <p>L'envoi email PDF (Resend) reste disponible mais n'est <strong>pas</strong> la facturation électronique réglementaire. Collez votre <strong>token API Pennylane</strong> (généré depuis Pennylane &gt; Connectivité &gt; Développeurs), puis cliquez « Enregistrer et tester la connexion ». Aucune facture n'est marquée « transmise » sans confirmation réelle de Pennylane.</p>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
        <div className="font-heading text-base font-semibold text-slate-900">Connexion Pennylane</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5 sm:col-span-2"><Label className="text-xs text-slate-500">Token API Pennylane</Label><Input data-testid="pennylane-api-token" type="password" autoComplete="new-password" value={cfg.api_token} onChange={(e) => setCfg({ ...cfg, api_token: e.target.value })} placeholder={status?.has_token ? "•••••••••••••• (enregistré — laisser vide pour conserver)" : "Collez votre token API Pennylane"} className="bg-slate-50 border-slate-200" /></div>
          <div className="space-y-1.5"><Label className="text-xs text-slate-500">Libellé entreprise</Label><Input data-testid="pennylane-company-label" value={cfg.company_label} onChange={(e) => setCfg({ ...cfg, company_label: e.target.value })} placeholder="Ex : Mon entreprise" className="bg-slate-50 border-slate-200" /></div>
          <div className="space-y-1.5"><Label className="text-xs text-slate-500">Environnement</Label>
            <Select value={cfg.environment} onValueChange={(v) => setCfg({ ...cfg, environment: v })}>
              <SelectTrigger data-testid="pennylane-environment" className="bg-slate-50 border-slate-200"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white border-slate-200 text-slate-700"><SelectItem value="production">Production</SelectItem><SelectItem value="sandbox">Sandbox (test)</SelectItem></SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={saveAndTest} disabled={saving || (!cfg.api_token && !status?.has_token)} data-testid="btn-save-test-pennylane" className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold disabled:opacity-60 disabled:cursor-not-allowed">{saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Enregistrer et tester la connexion</Button>
        </div>
        {testResult && (
          <div data-testid="pennylane-test-result" className={`text-sm rounded-lg px-3 py-2 ${testResult.ok ? "text-emerald-700 bg-emerald-50 border border-emerald-200" : "text-red-700 bg-red-50 border border-red-200"}`}>
            {testResult.ok
              ? <span>✅ Connecté à Pennylane{testResult.company ? ` — ${testResult.company}` : ""}{testResult.env ? ` (${testResult.env === "production" ? "Production" : "Sandbox"})` : ""}.</span>
              : <span>❌ Connexion impossible — {testResult.error}</span>}
          </div>
        )}
        <p className="text-[11px] text-slate-400">Chaque entreprise connecte son propre compte Pennylane avec un token API généré depuis Pennylane &gt; Connectivité &gt; Développeurs. Le token est stocké de manière sécurisée côté serveur et n'est jamais exposé côté client.</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-3" data-testid="einvoicing-suppliers">
        <div className="font-heading text-base font-semibold text-slate-900">Factures fournisseurs</div>
        <p className="text-sm text-slate-500">Réception et synchronisation des factures fournisseurs via Pennylane. Disponible une fois la connexion active.</p>
        <Button onClick={syncSuppliers} variant="outline" disabled={!connected} data-testid="btn-sync-suppliers" className="border-slate-200 disabled:opacity-60 disabled:cursor-not-allowed">Synchroniser les factures fournisseurs</Button>
      </div>

      <div>
        <div className="text-xs text-slate-500 mb-2">Statuts</div>
        <div className="flex flex-wrap gap-2">{LEGEND.map(([k, label, cls]) => <span key={k} className={`text-xs px-2.5 py-1 rounded-full font-medium ${cls}`}>{label}</span>)}</div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <div className="font-heading text-base font-semibold text-slate-900 mb-3">Historique des transmissions</div>
        {history.length === 0 ? (
          <p className="text-sm text-slate-500" data-testid="einvoicing-history-empty">Aucune transmission pour le moment.</p>
        ) : (
          <div className="divide-y divide-slate-100">
            {history.map((t) => (
              <div key={t.id} className="flex items-center justify-between py-2 text-sm">
                <span className="text-slate-900">{t.invoice_number || t.invoice_id}</span>
                <span className="text-slate-500">{t.status}{t.error ? ` — ${t.error}` : ""}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function PrivacyCard() {
  const { user, logout } = useAuth();
  const [pwd, setPwd] = useState("");
  const [busy, setBusy] = useState(false);

  const exportData = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/account/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `export-donnees-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export de vos données téléchargé");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const closeAccount = async () => {
    if (!pwd) return toast.error("Saisissez votre mot de passe pour confirmer");
    setBusy(true);
    try {
      const { data } = await api.post("/account/close", { password: pwd });
      toast.success(data.message || "Compte fermé");
      setTimeout(() => logout(), 2000);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <>
      <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="privacy-export-card">
        <h3 className="font-heading text-base font-semibold text-slate-900 mb-1">Exporter mes données (RGPD)</h3>
        <p className="text-sm text-slate-500 mb-4">Téléchargez l'ensemble des données de votre entreprise (clients, véhicules, devis, factures, encaissements, paramètres) au format JSON.</p>
        <Button onClick={exportData} disabled={busy} data-testid="btn-export-data" className="bg-slate-900 hover:bg-slate-800 text-white"><Download className="w-4 h-4 mr-1.5" /> Exporter mes données</Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-6 text-xs text-slate-500 space-y-1" data-testid="privacy-legal-links">
        <div className="font-heading text-sm font-semibold text-slate-900 mb-2">Documents légaux</div>
        <a href="/cgu" target="_blank" rel="noreferrer" className="block text-emerald-600 hover:underline">Conditions Générales d'Utilisation</a>
        <a href="/confidentialite" target="_blank" rel="noreferrer" className="block text-emerald-600 hover:underline">Politique de confidentialité</a>
        <a href="/mentions-legales" target="_blank" rel="noreferrer" className="block text-emerald-600 hover:underline">Mentions légales</a>
      </div>

      {user?.role === "owner" && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-6" data-testid="privacy-close-card">
          <h3 className="font-heading text-base font-semibold text-red-700 mb-1 flex items-center gap-2"><ShieldAlert className="w-4 h-4" /> Fermer mon compte</h3>
          <p className="text-sm text-red-800/80 mb-4">La fermeture désactive immédiatement l'accès de toute l'équipe. Vos données personnelles seront supprimées après un délai de 30 jours. Les factures et pièces comptables sont conservées 10 ans conformément à la loi.</p>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" data-testid="btn-open-close-account" className="border-red-500/40 bg-white text-red-600 hover:bg-red-500/10">Fermer définitivement mon compte</Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="bg-white border-slate-200">
              <AlertDialogHeader><AlertDialogTitle className="text-slate-900">Confirmer la fermeture du compte</AlertDialogTitle><AlertDialogDescription>Cette action est irréversible. Saisissez votre mot de passe pour confirmer.</AlertDialogDescription></AlertDialogHeader>
              <Input type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder="Votre mot de passe" data-testid="input-close-password" className="bg-slate-50 border-slate-200" />
              <AlertDialogFooter>
                <AlertDialogCancel className="bg-transparent border-slate-200">Annuler</AlertDialogCancel>
                <AlertDialogAction onClick={closeAccount} disabled={busy} data-testid="btn-confirm-close-account" className="bg-red-500 hover:bg-red-600">Fermer mon compte</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      )}
    </>
  );
}

function PublicLinkCard() {
  const [link, setLink] = useState(null);
  const [copied, setCopied] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [savingCfg, setSavingCfg] = useState(false);
  useEffect(() => {
    api.get("/my-public-link").then((r) => setLink(r.data)).catch(() => {});
    api.get("/settings").then((r) => {
      const s = r.data || {};
      setCfg({
        business_type: s.business_type || "",
        public_subtitle: s.public_subtitle || "",
        booking_fields: { vehicle: true, email: true, address: false, company: false, comment: true, service: true, ...(s.booking_fields || {}) },
        public_show_prices: s.public_show_prices !== false,
        public_allow_multi: s.public_allow_multi !== false,
        public_show_supplements: s.public_show_supplements !== false,
        public_enable_estimate: s.public_enable_estimate !== false,
      });
    }).catch(() => {});
  }, []);
  const url = link ? `${window.location.origin}${link.path}` : "";
  const copy = () => { try { navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch (e) {} };
  const toggle = (k) => setCfg((c) => ({ ...c, booking_fields: { ...c.booking_fields, [k]: !c.booking_fields[k] } }));
  const saveCfg = async () => {
    setSavingCfg(true);
    try { await api.put("/settings", cfg); } finally { setSavingCfg(false); }
  };
  const FIELDS = [["vehicle", "Véhicule (marque, modèle, immatriculation)"], ["email", "Email"], ["address", "Adresse d'intervention"], ["company", "Entreprise"], ["service", "Choix de la prestation"], ["comment", "Commentaire"]];
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4" data-testid="public-link-card">
      <div className="font-heading font-semibold text-slate-900 mb-1">Votre lien public</div>
      <p className="text-xs text-slate-500 mb-3">Partagez ce lien avec vos clients (site web, Google, réseaux, WhatsApp, QR code). Ils arrivent directement sur VOTRE page de réservation — jamais sur celle d'un autre professionnel.</p>
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        {link && <Input readOnly value={url} data-testid="public-link-url" onFocus={(e) => e.target.select()} className="bg-slate-50 border-slate-200 font-mono text-xs" />}
        <div className="flex gap-2 shrink-0">
          <Button type="button" variant="outline" onClick={copy} data-testid="public-link-copy" className="border-slate-200">{copied ? "Copié !" : "Copier"}</Button>
          <Button type="button" onClick={() => window.open(url, "_blank")} disabled={!url} data-testid="public-link-open" className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold">Ouvrir</Button>
        </div>
      </div>

      {cfg && (
        <div className="border-t border-slate-100 pt-4 space-y-3" data-testid="public-page-config">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Votre métier & votre page</div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label className="text-xs text-slate-500">Métier / activité</Label><Input data-testid="cfg-business-type" value={cfg.business_type} onChange={(e) => setCfg({ ...cfg, business_type: e.target.value })} placeholder="Ex : coiffure, plomberie, detailing, photographe…" className="bg-slate-50 border-slate-200" /></div>
            <div className="space-y-1.5"><Label className="text-xs text-slate-500">Sous-titre de la page</Label><Input data-testid="cfg-subtitle" value={cfg.public_subtitle} onChange={(e) => setCfg({ ...cfg, public_subtitle: e.target.value })} placeholder="Ex : Prise de rendez-vous en ligne" className="bg-slate-50 border-slate-200" /></div>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Champs demandés lors d'une réservation</Label>
            <div className="grid sm:grid-cols-2 gap-2 mt-2">
              {FIELDS.map(([k, lbl]) => (
                <label key={k} className="flex items-center gap-2 text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 cursor-pointer">
                  <input type="checkbox" data-testid={`cfg-field-${k}`} checked={!!cfg.booking_fields[k]} onChange={() => toggle(k)} />
                  {lbl}
                </label>
              ))}
            </div>
            <p className="text-[11px] text-slate-400 mt-2">Prénom, nom et téléphone sont toujours demandés. Désactivez « Véhicule » si votre métier n'en a pas besoin (coiffeur, artisan, photographe…).</p>
          </div>
          <div className="border-t border-slate-100 pt-3 space-y-2" data-testid="public-booking-options">
            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-400">Options de réservation & tarifs publics</Label>
            {[["public_show_prices", "Afficher les tarifs sur la page publique"], ["public_enable_estimate", "Activer l'estimation automatique (simulateur)"], ["public_allow_multi", "Autoriser plusieurs prestations"], ["public_show_supplements", "Afficher les suppléments"]].map(([k, lbl]) => (
              <label key={k} className="flex items-center justify-between text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                {lbl}
                <Switch checked={cfg[k] !== false} onCheckedChange={(val) => setCfg({ ...cfg, [k]: val })} data-testid={`cfg-${k}`} />
              </label>
            ))}
            <p className="text-[11px] text-slate-400">Une entreprise 100 % « sur devis » peut masquer les tarifs : la réservation reste possible sans afficher de faux prix.</p>
          </div>
          <Button type="button" onClick={saveCfg} disabled={savingCfg} data-testid="cfg-save" className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold">{savingCfg ? "Enregistrement…" : "Enregistrer ma page publique"}</Button>
        </div>
      )}
    </div>
  );
}


